import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconArrowCornerUpLeftOutline18 as ParentIcon,
	IconXmarkOutline18 as RemoveIcon,
} from "nucleo-ui-outline-18"

import type { Issue } from "@productlane/zero"
import { IssueStatusIcon, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { useRole } from "@/lib/useRole"
import { parentCandidates } from "./parent-candidates"
import { useSaveIssueToLinear } from "./use-save-issue-to-linear"

export const IssueParentSelect = ({
	issue,
	open: openProp,
	onOpenChange,
}: {
	issue: Issue
	open?: boolean
	onOpenChange?: (open: boolean) => void
}) => {
	const { role } = useRole()
	const [internalOpen, setInternalOpen] = useState(false)
	const open = openProp ?? internalOpen
	const setOpen = (next: boolean) => {
		setInternalOpen(next)
		onOpenChange?.(next)
	}
	const [search, setSearch] = useState("")
	const saveToLinear = useSaveIssueToLinear(issue.id)

	const [parent] = useQuery(
		queries.issueByLinearId(issue.parentLinearId ?? ""),
		{ enabled: !!issue.parentLinearId },
	)
	const [results] = useQuery(
		queries.searchIssues({ query: search, limit: 20 }),
		{ enabled: open },
	)
	const candidates = parentCandidates(issue, results)

	const trigger = (
		<PropertyTrigger
			tooltip="Change parent issue"
			icon={<ParentIcon className={propertyIconClassName} />}
		>
			{parent
				? `${parent.identifier} ${parent.title}`
				: issue.parentLinearId
					? "Parent not synced"
					: "Parent issue"}
		</PropertyTrigger>
	)

	if (role === "VIEWER") {
		return issue.parentLinearId ? trigger : null
	}

	const select = (parentLinearId: string | null) => {
		setOpen(false)
		setSearch("")
		void saveToLinear({ parentLinearId }, "issue-parent-update")
	}

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (!next) setSearch("")
			}}
			className="w-80 p-0"
			content={
				<Command shouldFilter={false}>
					<CommandInput
						showIcon={false}
						autoFocus
						placeholder="Set parent issue..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>No matching issues</CommandEmpty>
						<CommandGroup>
							{issue.parentLinearId && (
								<CommandItem
									value="remove-parent"
									onSelect={() => select(null)}
								>
									<div className="flex w-full items-center gap-2">
										<RemoveIcon className="h-4 w-4 flex-none" />
										<span className="flex-1 truncate">Remove parent</span>
									</div>
								</CommandItem>
							)}
							{candidates.map((candidate) => (
								<CommandItem
									key={candidate.id}
									value={candidate.id}
									onSelect={() => select(candidate.linearTicketId)}
								>
									<div className="flex w-full items-center gap-2">
										<IssueStatusIcon
											status={candidate.status}
											color={candidate.statusColor ?? ""}
											className="h-3.5 w-3.5 flex-none"
										/>
										<span className="flex-none text-tertiary">
											{candidate.identifier}
										</span>
										<span className="flex-1 truncate">{candidate.title}</span>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
		>
			{trigger}
		</Popover>
	)
}
